import { format, fromUnixTime, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
/**
 * Creates a unique cache key combining timezone and epoch seconds.
 * @param timeZone IANA timezone identifier
 * @param dateEpoch Unix timestamp in seconds
 */
const createCacheKey = (timeZone, dateEpoch) => `${timeZone}:${dateEpoch}`;
// Internal cache: key = "<timezone>:<epoch>", value = DateInfo
const dateInfoCache = new Map();
/**
 * Retrieves or computes date information for a given epoch timestamp and timezone.
 * Results are cached to avoid redundant calculations.
 * @param timeZone IANA timezone identifier (e.g. 'UTC', 'America/New_York')
 * @param dateEpoch Unix timestamp in seconds since epoch
 * @returns DateInfo containing zonedEpoch, dayStartEpoch, and yearMonth
 */
export function getDateInfo(timeZone, dateEpoch) {
    if (!Number.isFinite(dateEpoch)) {
        throw new Error(`Invalid dateEpoch: ${dateEpoch}`);
    }
    const key = createCacheKey(timeZone, dateEpoch);
    const cached = dateInfoCache.get(key);
    if (cached) {
        return cached;
    }
    // Convert epoch seconds to UTC Date, then to target timezone
    const utcDate = fromUnixTime(dateEpoch);
    const zonedDate = toZonedTime(utcDate, timeZone);
    if (isNaN(zonedDate.getTime())) {
        throw new Error(`Invalid timezone: ${timeZone}`);
    }
    // Compute start of day and formatted year-month
    const dayStart = startOfDay(zonedDate);
    const info = {
        zonedEpoch: zonedDate.getTime(),
        dayStartEpoch: dayStart.getTime(),
        yearMonth: format(zonedDate, 'yyyy-MM'),
    };
    dateInfoCache.set(key, info);
    return info;
}
/**
 * Clears all entries from the date info cache.
 */
export function clearDateInfoCache() {
    dateInfoCache.clear();
}
/**
 * Returns the number of entries currently in the cache.
 */
export function getDateInfoCacheSize() {
    return dateInfoCache.size;
}
